import { create } from 'zustand'
import { useEffect, useCallback } from 'react'
import { notificationsApi, NotificationWithActor } from '@/lib/api'

interface NotificationsState {
  notifications: NotificationWithActor[]
  unreadCount: number
  unseenCount: number
  isLoading: boolean
  error: string | null
  lastFetchTime: number

  // Actions
  fetchNotifications: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
  clearError: () => void
}

export const useNotificationsStore = create<NotificationsState>()((set, get) => ({
  notifications: [],
  unreadCount: 0,
  unseenCount: 0,
  isLoading: false,
  error: null,
  lastFetchTime: 0,

  fetchNotifications: async () => {
    set({ isLoading: true, error: null })
    try {
      const response = await notificationsApi.getNotifications(50, 0)
      set({
        notifications: response.data,
        isLoading: false,
        lastFetchTime: Date.now(),
      })
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch notifications'
      set({ error: message, isLoading: false })
    }
  },

  fetchUnreadCount: async () => {
    try {
      const response = await notificationsApi.getUnreadCount()
      set({
        unreadCount: response.data.unread_count,
        unseenCount: response.data.unseen_count,
      })
    } catch (error: unknown) {
      // Silent fail for count updates
      console.error('Failed to fetch unread count:', error)
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      await notificationsApi.markAsRead(notificationId)

      // Update local state
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.notification.id === notificationId
            ? {
                ...n,
                notification: {
                  ...n.notification,
                  is_read: true,
                  read_at: new Date().toISOString(),
                },
              }
            : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }))
    } catch (error: unknown) {
      console.error('Failed to mark notification as read:', error)
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationsApi.markAllAsRead()

      // Update local state
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          notification: {
            ...n.notification,
            is_read: true,
            read_at: new Date().toISOString(),
          },
        })),
        unreadCount: 0,
      }))
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to mark all as read'
      set({ error: message })
    }
  },

  clearError: () => set({ error: null }),
}))

// Hook for easy access with auto-polling
export function useNotifications() {
  const store = useNotificationsStore()

  // Fetch notifications on mount
  useEffect(() => {
    store.fetchNotifications()
    store.fetchUnreadCount()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      store.fetchUnreadCount()
    }, 30000) // 30 seconds

    return () => clearInterval(interval)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh function
  const refresh = useCallback(() => {
    store.fetchNotifications()
    store.fetchUnreadCount()
  }, [store])

  return {
    ...store,
    refresh,
  }
}

export default useNotifications
